import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import {
  ExclamationCircleIcon,
  ClockIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import DisabledTextInput from "../forms/DisabledTextInput";
import DangerZone from "../forms/DangerZone";
import {
  createPhotoAlbum,
  deletePhotoAlbums,
  getPhotoAlbums,
  updatePhotoAlbum,
} from "../../slices/content";
import Dropdown from "../forms/Dropdown";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import SwitchToggle from "../forms/SwitchToggle";
import Indicator from "../layout/Indicator";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function PhotoAlbums() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getPhotoAlbums());
    dispatch(getRooms());
  }, [dispatch]);

  // Set state
  const [selectedAlbum, setSelectedAlbum] = useState<{
    id: string;
    created_by: string;
    last_updated_by: string;
    datetime_created: string;
    datetime_updated: string | null;
    name: { [key: string]: string };
    rooms: string[];
    photos: Array<{
      id: string;
      uploaded_by: string;
      last_updated_by: string;
      datetime_uploaded: string;
      datetime_updated: string | null;
      url: string;
      description: string | null;
    }>;
    status: string;
  } | null>(null);
  const [selectedAlbumChooseRoomsBy, setSelectedAlbumChooseRoomsBy] = useState<{
    value: string;
  }>({ value: "name" });
  const [selectedAlbumTags, setSelectedAlbumTags] = useState<string[]>([]);
  const [createAlbumForm, setCreateAlbumForm] = useState<{
    name: { [key: string]: string };
    choose_rooms_by: string;
    tags: string[];
    rooms: string[];
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    choose_rooms_by: "name",
    tags: [],
    rooms: [],
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { photo_albums: photoAlbums } = useAppSelector(
    (state) => state.content,
  );
  const { rooms } = useAppSelector((state) => state.rooms);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectAlbumToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [albumId] = args;
    const albumData = albumsData.filter((album) => album.id === albumId)[0];
    setSelectedAlbum({ ...albumData, status: albumData.status[0] });
    setShowEditModal(true);
  }

  function openCreateAlbumModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedAlbum === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedAlbum({ ...selectedAlbum, rooms: allAvailableRooms });
    } else {
      setSelectedAlbum({ ...selectedAlbum, rooms: [] });
    }
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateAlbumForm({ ...createAlbumForm, rooms: allAvailableRooms });
    } else {
      setCreateAlbumForm({ ...createAlbumForm, rooms: [] });
    }
  }

  function updateRoomForAlbum(roomId: string) {
    if (selectedAlbum === null) return;
    if (selectedAlbum.rooms.includes(roomId)) {
      const updatedRooms = selectedAlbum.rooms.filter((x) => x !== roomId);
      setSelectedAlbum({ ...selectedAlbum, rooms: updatedRooms });
    } else {
      const updatedRooms = [...selectedAlbum.rooms, roomId];
      setSelectedAlbum({ ...selectedAlbum, rooms: updatedRooms });
    }
  }

  function updateRoomForCreateAlbum(roomId: string) {
    if (createAlbumForm.rooms.includes(roomId)) {
      const updatedRooms = createAlbumForm.rooms.filter((x) => x !== roomId);
      setCreateAlbumForm({ ...createAlbumForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createAlbumForm.rooms, roomId];
      setCreateAlbumForm({ ...createAlbumForm, rooms: updatedRooms });
    }
  }

  async function confirmCreateAlbum(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    const {
      name,
      choose_rooms_by: chooseRoomsBy,
      tags,
      rooms,
    } = createAlbumForm;

    // Validate text fields
    if (name[language].length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 3000),
      );
      return;
    }

    // Build and validate rooms array
    let albumRooms = rooms;
    if (chooseRoomsBy === "tag") {
      if (tags.length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_tags", language), "danger", 3000),
        );
        return;
      }
      albumRooms = [];
      for (let i = 0; i < roomsData.length; i++) {
        const roomTags = roomsData[i].tag;
        const commonTags = roomTags.filter((x: any) => tags.includes(x));
        if (commonTags.length > 0) {
          albumRooms.push(roomsData[i].id);
        }
      }
    }
    if (albumRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 3000),
      );
      return;
    }

    // Build request body
    const obj = {
      name,
      property: user.property.id,
      rooms: albumRooms,
      status: "A",
    };

    setShowCreateModal(false);
    const res: any = await dispatch(createPhotoAlbum(obj));
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_photo_album", language),
          "success",
          3000,
        ),
      );
      setCreateAlbumForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        choose_rooms_by: "name",
        tags: [],
        rooms: [],
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditAlbum(e: any) {
    e.preventDefault();
    if (selectedAlbum === null) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    const { id, name, rooms, status } = selectedAlbum;
    const { value: chooseRoomsBy } = selectedAlbumChooseRoomsBy;

    // Validate text fields
    if (name[language].length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 3000),
      );
      return;
    }

    // Build and validate rooms array
    let albumRooms = rooms;
    if (chooseRoomsBy === "tag") {
      if (selectedAlbumTags.length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_tags", language), "danger", 3000),
        );
        return;
      }
      albumRooms = [];
      for (let i = 0; i < roomsData.length; i++) {
        const roomTags = roomsData[i].tag;
        const commonTags = roomTags.filter((x: any) =>
          selectedAlbumTags.includes(x),
        );
        if (commonTags.length > 0) {
          albumRooms.push(roomsData[i].id);
        }
      }
    }
    if (albumRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 3000),
      );
      return;
    }

    // Build object for saved photos
    const savedPhotos: any = selectedAlbum.photos
      .filter((x: any) => !images.photo_albums.removed.includes(x.id))
      .map((x: any) => ({
        id: x.id,
        source: "saved",
        data: null,
      }));

    // Build object for processed photos
    const processedPhotoKeys = Object.keys(images.photo_albums.data);
    const processedPhotos: any = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.photo_albums.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    // Combine saved and processed photos
    const allPhotos = savedPhotos.concat(processedPhotos);

    // Build request body
    const obj = {
      albumId: id,
      name,
      property: user.property.id,
      photos: allPhotos,
      rooms: albumRooms,
      status,
    };

    setShowEditModal(false);
    const res: any = await dispatch(updatePhotoAlbum(obj));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_photo_album", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteAlbum(e: any) {
    e.preventDefault();
    if (selectedAlbum === null) return;
    const albumId = selectedAlbum.id;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (albumId === null || albumId === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_campaign", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deletePhotoAlbums({ albumIds: [albumId] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function selectOrUnselectAllTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setSelectedAlbumTags(allAvailableTags);
    } else {
      setSelectedAlbumTags([]);
    }
  }

  function selectOrUnselectAllCreateTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setCreateAlbumForm({ ...createAlbumForm, tags: allAvailableTags });
    } else {
      setCreateAlbumForm({ ...createAlbumForm, tags: [] });
    }
  }

  function updateTagForAlbum(tag: string) {
    if (selectedAlbum === null) return;
    if (selectedAlbumTags.includes(tag)) {
      const updatedTags = selectedAlbumTags.filter((x) => x !== tag);
      setSelectedAlbumTags(updatedTags);
    } else {
      const updatedTags = [...selectedAlbumTags, tag];
      setSelectedAlbumTags(updatedTags);
    }
  }

  function updateTagForCreateAlbum(tag: string) {
    if (createAlbumForm.tags.includes(tag)) {
      const updatedTags = selectedAlbumTags.filter((x) => x !== tag);
      setCreateAlbumForm({ ...createAlbumForm, tags: updatedTags });
    } else {
      const updatedTags = [...createAlbumForm.tags, tag];
      setCreateAlbumForm({ ...createAlbumForm, tags: updatedTags });
    }
  }

  function getSavedImages() {
    if (selectedAlbum === null) return [];
    return selectedAlbum.photos
      .filter((x: any) => !images.photo_albums.removed.includes(x.id))
      .map((x: any) => ({ id: x.id, data: x.url }));
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const albumsData = photoAlbums.data === null ? [] : photoAlbums.data;
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];

  // Create tags object for filters
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("photo_albums_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateAlbumModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_photo_album", language)}
              </Button>
            }
          />
        }
      />

      {/* Create album modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_photo_album", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateAlbum}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_photo_album", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createAlbumForm}
                stateName="name"
                onChange={setCreateAlbumForm}
                defaultLanguage={language}
                languageChoices={
                  albumsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(albumsData[0].name)
                }
              />

              {/* Choose rooms by */}
              <Dropdown
                id="choose_rooms_by"
                className="col-span-full"
                label={translateText("choose_rooms_by", language)}
                small={translateText("choose_rooms_by_subtext", language)}
                showLabel={true}
                required={true}
                state={createAlbumForm}
                stateName="choose_rooms_by"
                onChange={setCreateAlbumForm}
                defaultValue="name"
              >
                {chooseRoomsBy.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Rooms */}
              {createAlbumForm.choose_rooms_by === "name" ? (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllCreateRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForCreateAlbum}
                        checked={createAlbumForm.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("tag", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText(
                        "select_tags_for_rooms_campaign",
                        language,
                      )}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllCreateTags}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomTags.map((tag: any) => (
                      <Checkbox
                        key={tag.id}
                        id={tag.id}
                        onChange={updateTagForCreateAlbum}
                        checked={createAlbumForm.tags.includes(tag.id)}
                        label={tag.label}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit album modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_photo_album", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditAlbum}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`Edit photo album "${selectedAlbum?.name[language]}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created by */}
              <DisabledTextInput
                id="created_by"
                label={translateText("created_by", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedAlbum === null
                    ? ""
                    : `${selectedAlbum.created_by} @ ${selectedAlbum.datetime_created}`
                }
              />

              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedAlbum}
                stateName="name"
                onChange={setSelectedAlbum}
                defaultLanguage={language}
                languageChoices={
                  albumsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(albumsData[0].name)
                }
              />

              {/* Images */}
              <div>
                <label
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("images", language)}
                </label>
                <UploadFileDropzone
                  id="photo_album_images"
                  className=""
                  limit={20}
                  page="photo_albums"
                  savedImages={getSavedImages()}
                  blockId={null}
                />
              </div>

              {/* Choose rooms by */}
              <Dropdown
                id="choose_rooms_by"
                className="col-span-full"
                label={translateText("choose_rooms_by", language)}
                small={translateText("choose_rooms_by_subtext", language)}
                showLabel={true}
                required={true}
                state={selectedAlbumChooseRoomsBy}
                stateName="value"
                onChange={setSelectedAlbumChooseRoomsBy}
                defaultValue="name"
              >
                {chooseRoomsBy.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Rooms */}
              {selectedAlbumChooseRoomsBy.value === "name" ? (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  {selectedAlbum !== null && (
                    <>
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllRooms}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomsData.map((room: any) => (
                          <Checkbox
                            key={room.id}
                            id={room.id}
                            onChange={updateRoomForAlbum}
                            checked={selectedAlbum.rooms.includes(room.id)}
                            label={room.name}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("tag", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText(
                        "select_tags_for_rooms_campaign",
                        language,
                      )}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  {selectedAlbum !== null && (
                    <>
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllTags}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomTags.map((tag: any) => (
                          <Checkbox
                            key={tag.id}
                            id={tag.id}
                            onChange={updateTagForAlbum}
                            checked={selectedAlbumTags.includes(tag.id)}
                            label={tag.label}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_album_subtext", language)}
                showLabel={true}
                toggle={
                  selectedAlbum === null ? false : selectedAlbum.status === "A"
                }
                state={selectedAlbum}
                stateName="status"
                handler={setSelectedAlbum}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              {selectedAlbum !== null && (
                <DangerZone
                  objectId={selectedAlbum.id}
                  handler={confirmDeleteAlbum}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {photoAlbums.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {albumsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {albumsData.map((album) => (
                    <a
                      href="#!"
                      key={album.id}
                      onClick={(e) => selectAlbumToEdit(e, [album.id])}
                      className="relative"
                    >
                      <Card
                        key={album.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {album.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <PhotoIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {`${album.photos.length} ${
                              album.photos.length === 1
                                ? translateText("photo", language, true)
                                : translateText("photos", language, true)
                            }`}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {album.datetime_created}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={album.status[0] === "A" ? "success" : "warning"}
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_photo_albums", language)}
                subtext={translateText("no_photo_albums_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
